import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { DataTable } from "@/components/DataTable";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Paperclip, Search, X } from "lucide-react";
import { fmtDateTime, fmtCurrency, fmtCost, extractName, emailDomain, fmtDate } from "@/lib/format";
import { rememberEmailList } from "@/lib/emailNav";

const CATS = ["rfq", "quote", "sales", "lead", "inquiry", "noise"];
const STATUSES = ["pending", "processed", "skipped", "error"];

export default function EmailsPage() {
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const q              = params.get("q")              || "";
  const category       = params.get("category")       || "";
  const status         = params.get("status")         || "";
  const has_att        = params.get("has_attachment") || "";
  const company        = params.get("company")        || "";
  const opportunity_id = params.get("opportunity_id") || "";
  const date_from      = params.get("date_from")      || "";
  const date_to        = params.get("date_to")        || "";
  const page           = parseInt(params.get("page") || "1", 10);
  const limit          = 50;

  useEffect(() => {
    setLoading(true);
    const qp = { limit, offset: (page - 1) * limit };
    if (q)              qp.q = q;
    if (category)       qp.category = category;
    if (status)         qp.status = status;
    if (has_att)        qp.has_attachment = has_att;
    if (company)        qp.company = company;
    if (opportunity_id) qp.opportunity_id = opportunity_id;
    if (date_from)      qp.date_from = date_from;
    if (date_to)        qp.date_to = date_to;
    api.get("/emails", { params: qp })
      .then((r) => {
        setData(r.data);
        // Remember the visible list so EmailDetail can navigate with J/K.
        rememberEmailList((r.data?.items || []).map((e) => e.id));
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);

  const setParam = (k, v) => {
    const next = new URLSearchParams(params);
    if (!v) next.delete(k); else next.set(k, v);
    if (k !== "page") next.set("page", "1");
    setParams(next);
  };
  const removeParam = (k) => {
    const next = new URLSearchParams(params);
    next.delete(k);
    next.set("page", "1");
    setParams(next);
  };
  const reset = () => setParams(new URLSearchParams());

  const total = data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / limit));

  const chips = [
    company        && { k: "company",        label: `company: ${company}` },
    opportunity_id && { k: "opportunity_id", label: `opp: ${opportunity_id}` },
    date_from      && { k: "date_from",      label: `from ${fmtDate(date_from)}` },
    date_to        && { k: "date_to",        label: `to ${fmtDate(date_to)}` },
  ].filter(Boolean);

  const columns = useMemo(() => [
    {
      accessorKey: "ingested_at",
      header: "Received",
      size: 170,
      cell: ({ getValue }) => <span className="mono" style={{ fontSize: 13 }}>{fmtDateTime(getValue())}</span>,
    },
    {
      accessorKey: "from_addr",
      header: "From",
      enableSorting: false,
      size: 220,
      cell: ({ row }) => (
        <div className="truncate">
          <div className="truncate">{extractName(row.original.from_addr)}</div>
          <div className="subtext mono truncate">{emailDomain(row.original.from_addr)}</div>
        </div>
      ),
    },
    {
      accessorKey: "company",
      header: "Company",
      size: 200,
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? <span className="truncate block">{v}</span> : <span className="meta">—</span>;
      },
    },
    {
      accessorKey: "subject",
      header: "Subject",
      size: 400,
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? <span className="truncate block">{v}</span> : <span className="meta">(no subject)</span>;
      },
    },
    {
      accessorKey: "category",
      header: "Category",
      size: 110,
      cell: ({ getValue }) => {
        const v = getValue();
        return v
          ? <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 uppercase text-[10px] tracking-wider font-semibold">{v}</span>
          : <span className="meta">—</span>;
      },
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 120,
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    },
    {
      accessorKey: "amount",
      header: "Amount",
      meta: { align: "right" },
      size: 120,
      cell: ({ getValue }) => {
        const v = getValue();
        return v ? <span className="num strong">{fmtCurrency(v, true)}</span> : <span className="meta">—</span>;
      },
    },
    {
      accessorKey: "opportunity_id",
      header: "Opportunity",
      enableSorting: false,
      size: 150,
      cell: ({ getValue }) => {
        const v = getValue();
        return v
          ? <span className="mono text-accent truncate block" style={{ fontSize: 12 }}>{v}</span>
          : <span className="meta">—</span>;
      },
    },
    {
      accessorKey: "llm_cost_usd",
      header: "LLM $",
      meta: { align: "right" },
      size: 90,
      cell: ({ row }) => {
        const v = row.original.llm_cost_usd;
        return (
          <span className="meta" title={row.original.llm_input_tokens
            ? `${row.original.llm_input_tokens} in / ${row.original.llm_output_tokens} out` : ""}>
            {fmtCost(v)}
          </span>
        );
      },
    },
    {
      accessorKey: "attachment_count",
      header: "Att",
      meta: { align: "center" },
      size: 70,
      cell: ({ getValue }) => {
        const v = getValue();
        return v > 0 ? (
          <span className="inline-flex items-center gap-1 mono" style={{ fontSize: 12 }}>
            <Paperclip size={12} />{v}
          </span>
        ) : null;
      },
    },
  ], []);

  return (
    <div className="p-8 max-w-[1700px]" data-testid="emails-page">
      <PageHeader
        eyebrow="Inbox audit"
        title="Emails"
        subtitle={`Every email the agent has seen. ${total ? total.toLocaleString() : "—"} total.`}
      />

      {chips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="filter-chips">
          {chips.map((c) => (
            <button
              key={c.k}
              onClick={() => removeParam(c.k)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-white text-xs font-mono"
              style={{ background: "hsl(var(--accent-primary))" }}
              data-testid={`chip-${c.k}`}
            >
              {c.label}<X size={12} />
            </button>
          ))}
        </div>
      )}

      <div className="card-surface p-3 mb-4 flex flex-wrap items-center gap-2" data-testid="filters-bar">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" size={14} />
          <Input
            data-testid="search-input"
            placeholder="Search subject, sender, body…"
            defaultValue={q}
            onKeyDown={(e) => e.key === "Enter" && setParam("q", e.currentTarget.value)}
            className="h-9 pl-8 text-sm"
          />
        </div>

        <FilterSelect testId="filter-category" value={category} onChange={(v) => setParam("category", v)} placeholder="Category">
          {CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </FilterSelect>
        <FilterSelect testId="filter-status" value={status} onChange={(v) => setParam("status", v)} placeholder="Status">
          {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
        </FilterSelect>
        <FilterSelect testId="filter-attachment" value={has_att} onChange={(v) => setParam("has_attachment", v)} placeholder="Attachments">
          <SelectItem value="true">With attachments</SelectItem>
          <SelectItem value="false">No attachments</SelectItem>
        </FilterSelect>

        {(q || category || status || has_att || chips.length > 0) && (
          <Button data-testid="clear-filters-btn" variant="ghost" size="sm" onClick={reset} className="h-9">
            <X size={14} className="mr-1" /> Clear all
          </Button>
        )}
      </div>

      <DataTable
        data-testid="emails-table"
        columns={columns}
        data={data?.items || []}
        onRowClick={(e) => nav(`/emails/${e.id}`)}
        getRowTestId={(e) => `email-row-${e.id}`}
        emptyMessage={loading ? "Loading…" : "No emails match."}
        initialSorting={[{ id: "ingested_at", desc: true }]}
      />

      <div className="flex items-center justify-between mt-4 text-sm">
        <span className="meta mono">
          {total ? `Page ${page} of ${pages} · ${total} emails` : ""}
        </span>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" data-testid="page-prev"
            disabled={page <= 1} onClick={() => setParam("page", String(page - 1))}>← Prev</Button>
          <Button variant="outline" size="sm" data-testid="page-next"
            disabled={page >= pages} onClick={() => setParam("page", String(page + 1))}>Next →</Button>
        </div>
      </div>
    </div>
  );
}

function FilterSelect({ value, onChange, placeholder, children, testId }) {
  return (
    <Select value={value || "__all__"} onValueChange={(v) => onChange(v === "__all__" ? "" : v)}>
      <SelectTrigger data-testid={testId} className="h-9 w-[170px] text-sm">
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">{placeholder} — any</SelectItem>
        {children}
      </SelectContent>
    </Select>
  );
}
